import type { IAxisValueOption } from '@atlas/types'
import { EmptyState, SectionCard } from '@atlas/ui-components'
import { cn } from '@atlas/ui-primitives'
import { Search } from 'lucide-react'
import { ProductCard } from './ProductCard'

/** A product prepared for display: real fields plus the audiences it is tagged with. */
export interface ProductIndexItem {
	productId: string
	name: string
	meta: string
	/** The product's image, where one is classified to it. No placeholder stands in for its absence. */
	imageUrl?: string | null
	/** Audience value codes from the product's own tags. Empty = unclassified, not universal. */
	audienceCodes: string[]
}

/** The synthesised "everything" pill. Not an audience value — hence the sentinel. */
export const ALL_AUDIENCES = '__all__'

export interface ProductIndexProps {
	products: ProductIndexItem[]
	/** The `audience` axis values, straight from the API — never a list hardcoded here. */
	audiences: IAxisValueOption[]
	query: string
	onQueryChange: (query: string) => void
	/** Selected audience code, or `ALL_AUDIENCES`. */
	audience: string
	onAudienceChange: (audience: string) => void
	/** The page's market, carried into each product link so the product opens resolved for it. */
	market?: string | null
}

/**
 * The brand's products, with free-text search and taxonomy-driven audience pills.
 *
 * A product with NO audience tag matches only "All audiences": showing it under every
 * pill would report a classification gap as coverage.
 */
export function ProductIndex({
	products,
	audiences,
	query,
	onQueryChange,
	audience,
	onAudienceChange,
	market,
}: ProductIndexProps) {
	const needle = query.trim().toLowerCase()
	const visible = products.filter(
		(p) =>
			(needle === '' || `${p.name} ${p.meta}`.toLowerCase().includes(needle)) &&
			(audience === ALL_AUDIENCES || p.audienceCodes.includes(audience)),
	)

	const pills: IAxisValueOption[] = [{ code: ALL_AUDIENCES, label: 'All audiences' }, ...audiences]

	return (
		<SectionCard
			title="Product index"
			action={
				<div className="relative w-full max-w-64">
					<Search aria-hidden className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-subtle" />
					<input
						value={query}
						onChange={(e) => onQueryChange(e.target.value)}
						placeholder="Search products…"
						aria-label="Search products"
						className="w-full rounded-ds-full border border-ds-border bg-ds-surface py-1.5 pl-9 pr-4 text-sm text-ds-text placeholder:text-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
					/>
				</div>
			}
		>
			<div className="mb-4 flex flex-wrap gap-1.5">
				{pills.map((a) => (
					<button
						key={a.code}
						type="button"
						aria-pressed={audience === a.code}
						onClick={() => onAudienceChange(a.code)}
						className={cn(
							'rounded-ds-full px-3 py-1 text-sm transition-colors',
							audience === a.code
								? 'bg-ds-primary text-ds-background'
								: 'border border-ds-border bg-ds-surface text-ds-text hover:border-ds-text',
						)}
					>
						{a.label}
					</button>
				))}
			</div>

			{products.length === 0 ? (
				<p className="text-sm text-ds-text-muted">
					No products are registered for this brand yet — nothing to consult here.
				</p>
			) : visible.length === 0 ? (
				<EmptyState title="No products match." variant="filtered" />
			) : (
				// Three across, not four: the card carries a thumbnail and two lines of real
				// detail now, which four columns crop.
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{visible.map((p) => (
						<ProductCard
							key={p.productId}
							productId={p.productId}
							name={p.name}
							meta={p.meta}
							imageUrl={p.imageUrl}
							market={market}
						/>
					))}
				</div>
			)}
		</SectionCard>
	)
}
